import React, { useState } from 'react';
import { Bell, Save, BookOpen, Calendar, MessageSquare, BarChart2, FileText, Megaphone, Info, Tag, HelpCircle } from 'lucide-react';
import AuroraFloatingCard from '../ui/AuroraFloatingCard';
interface NotificationSetting {
  id: string;
  title: string;
  description: string;
  enabled: boolean;
  icon: React.ReactNode;
}
export function NotificationSettings() {
  const [allNotificationsEnabled, setAllNotificationsEnabled] = useState(true);
  const [settings, setSettings] = useState<NotificationSetting[]>([{
    id: 'homework_deadline',
    title: 'Ödev Son Teslim Tarihi Yaklaşınca',
    description: 'Öğrencilerin ödevleri için son teslim tarihinden 24 saat önce bildirim alın.',
    enabled: true,
    icon: <BookOpen className="w-5 h-5" data-id="element-837" />
  }, {
    id: 'daily_program',
    title: 'Gün Sonunda Programda Eksik Varsa',
    description: 'Günlük programda tamamlanmamış öğeler varsa gün sonunda hatırlatma alın.',
    enabled: true,
    icon: <Calendar className="w-5 h-5" data-id="element-838" />
  }, {
    id: 'new_homework',
    title: 'Yeni Ödev Eklendiğinde',
    description: 'Öğretmenler tarafından yeni bir ödev eklendiğinde bildirim alın.',
    enabled: true,
    icon: <BookOpen className="w-5 h-5" data-id="element-839" />
  }, {
    id: 'new_message',
    title: 'Yeni Mesaj Geldiğinde',
    description: 'Öğretmen, öğrenci veya velilerden yeni bir mesaj aldığınızda bildirim alın.',
    enabled: true,
    icon: <MessageSquare className="w-5 h-5" data-id="element-840" />
  }, {
    id: 'weekly_report',
    title: 'Haftalık İlerleme Raporu Hazır Olduğunda',
    description: 'Öğrencilerin haftalık ilerleme raporları hazır olduğunda bildirim alın.',
    enabled: true,
    icon: <BarChart2 className="w-5 h-5" data-id="element-841" />
  }, {
    id: 'exam_results',
    title: 'Deneme Sınavı Sonuçları Açıklandığında',
    description: 'Deneme sınavlarının sonuçları açıklandığında bildirim alın.',
    enabled: true,
    icon: <FileText className="w-5 h-5" data-id="element-842" />
  }, {
    id: 'forum_reply',
    title: 'Forumdaki Soruya Cevap Geldiğinde',
    description: 'Açtığınız forum konularına veya yanıtladığınız sorulara yeni bir cevap geldiğinde bildirim alın.',
    enabled: true,
    icon: <MessageSquare className="w-5 h-5" data-id="element-843" />
  }, {
    id: 'platform_updates',
    title: 'Platformdaki Yenilikler ve Güncellemeler',
    description: 'Platformdaki yeni özellikler ve güncellemeler hakkında bildirim alın.',
    enabled: false,
    icon: <Info className="w-5 h-5" data-id="element-844" />
  }, {
    id: 'special_offers',
    title: 'İndirimler ve Özel Teklifler',
    description: 'Özel indirimler, kampanyalar ve teklifler hakkında bildirim alın.',
    enabled: false,
    icon: <Tag className="w-5 h-5" data-id="element-845" />
  }]);
  const handleToggleAll = () => {
    const newState = !allNotificationsEnabled;
    setAllNotificationsEnabled(newState);
    setSettings(settings.map(setting => ({
      ...setting,
      enabled: newState
    })));
  };
  const handleToggleSetting = (id: string) => {
    const newSettings = settings.map(setting => setting.id === id ? {
      ...setting,
      enabled: !setting.enabled
    } : setting);
    setSettings(newSettings);
    // Update the main toggle based on whether all settings are enabled
    setAllNotificationsEnabled(newSettings.every(setting => setting.enabled));
  };
  const handleSaveSettings = () => {
    // In a real app, this would save the settings to a backend
    alert('Bildirim ayarlarınız başarıyla kaydedildi.');
  };
  return <div className="space-y-6" data-id="element-846">
      <div className="flex items-center gap-3" data-id="element-847">
        <h1 className="text-3xl font-bold" data-id="element-848">Bildirim Ayarları</h1>
        <Bell className="w-8 h-8 text-orange-400" data-id="element-849" />
      </div>
      <AuroraFloatingCard data-id="element-850">
        <div className="p-6" data-id="element-851">
          {/* Main toggle switch */}
          <div className="flex items-center justify-between p-4 mb-6 rounded-lg bg-black/50 border border-purple-500/30" data-id="element-852">
            <div className="flex items-center gap-4" data-id="element-853">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${allNotificationsEnabled ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-800 text-gray-500'}`} data-id="element-854">
                <Bell className="w-6 h-6" data-id="element-855" />
              </div>
              <div data-id="element-856">
                <h3 className="text-lg font-medium text-white" data-id="element-857">
                  Tüm Bildirimler
                </h3>
                <p className="text-sm text-gray-400" data-id="element-858">
                  Tüm bildirimleri açın veya kapatın
                </p>
              </div>
            </div>
            <div className="relative" data-id="element-859">
              <input type="checkbox" id="toggle-all" checked={allNotificationsEnabled} onChange={handleToggleAll} className="sr-only" data-id="element-860" />
              <label htmlFor="toggle-all" className={`block w-14 h-7 rounded-full transition-colors cursor-pointer ${allNotificationsEnabled ? 'bg-gradient-to-r from-purple-600 to-indigo-600' : 'bg-gray-700'}`} data-id="element-861">
                <span className={`block w-5 h-5 mt-1 ml-1 rounded-full transition-transform duration-300 ease-in-out bg-white ${allNotificationsEnabled ? 'transform translate-x-7' : ''}`} data-id="element-862"></span>
              </label>
            </div>
          </div>
          {/* Individual notification settings */}
          <div className="space-y-4" data-id="element-863">
            {settings.map(setting => <div key={setting.id} className="flex items-center justify-between p-4 rounded-lg bg-black/50 border border-white/10 hover:border-purple-500/30 transition-colors" data-id="element-864">
                <div className="flex items-center gap-4" data-id="element-865">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${setting.enabled ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-800 text-gray-500'}`} data-id="element-866">
                    {setting.icon}
                  </div>
                  <div data-id="element-867">
                    <h3 className="font-medium text-white" data-id="element-868">{setting.title}</h3>
                    <p className="text-sm text-gray-400" data-id="element-869">
                      {setting.description}
                    </p>
                  </div>
                </div>
                <div className="relative" data-id="element-870">
                  <input type="checkbox" id={`toggle-${setting.id}`} checked={setting.enabled} onChange={() => handleToggleSetting(setting.id)} className="sr-only" data-id="element-871" />
                  <label htmlFor={`toggle-${setting.id}`} className={`block w-12 h-6 rounded-full transition-colors cursor-pointer ${setting.enabled ? 'bg-gradient-to-r from-purple-600 to-indigo-600' : 'bg-gray-700'}`} data-id="element-872">
                    <span className={`block w-4 h-4 mt-1 ml-1 rounded-full transition-transform duration-300 ease-in-out bg-white ${setting.enabled ? 'transform translate-x-6' : ''}`} data-id="element-873"></span>
                  </label>
                </div>
              </div>)}
          </div>
          {/* Help text */}
          <div className="mt-6 p-4 rounded-lg bg-blue-500/10 border border-blue-500/30 flex gap-3" data-id="element-874">
            <div className="flex-shrink-0" data-id="element-875">
              <HelpCircle className="w-5 h-5 text-blue-400" data-id="element-876" />
            </div>
            <p className="text-sm text-blue-300" data-id="element-877">
              Bildirim tercihlerinizi istediğiniz zaman değiştirebilirsiniz.
              E-posta bildirimleri için lütfen profil ayarlarınızı kontrol edin.
            </p>
          </div>
          {/* Save button */}
          <div className="mt-8 flex justify-end" data-id="element-878">
            <button onClick={handleSaveSettings} className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg text-white font-medium flex items-center gap-2 hover:opacity-90 transition-opacity" data-id="element-879">
              <Save className="w-5 h-5" data-id="element-880" />
              <span data-id="element-881">Değişiklikleri Kaydet</span>
            </button>
          </div>
        </div>
      </AuroraFloatingCard>
    </div>;
}